import { Clock } from 'lucide-react';
import HeaderSearchRow from './HeaderSearchRow';

const Note = ({ testid, children }) => (
  <div className="px-3 py-3 text-xs" style={{ color: 'var(--strata-text-muted)' }} data-testid={testid}>{children}</div>
);
const GroupHead = ({ children, right }) => (
  <div className="flex items-center justify-between px-3 pt-2.5 pb-1 text-[10px] uppercase tracking-[0.14em] font-semibold" style={{ color: 'var(--strata-text-muted)' }}>
    <span>{children}</span>{right}
  </div>
);
const optClass = (on) => `px-3 py-2 text-xs cursor-pointer transition-colors ${on ? 'bg-[#4575A8]/10' : ''}`;
const nounList = (sources) => {
  const n = sources.map((s) => s.noun);
  return n.length > 1 ? `${n.slice(0, -1).join(', ')} or ${n[n.length - 1]}` : n[0];
};

const Option = ({ i, active, onPick, onHover, testid, children, extra }) => (
  <li id={`topbar-search-opt-${i}`} role="option" aria-selected={active === i} data-testid={testid}
    onMouseDown={(e) => e.preventDefault()} onClick={onPick} onMouseEnter={onHover} className={`${optClass(active === i)} ${extra || ''}`}>
    {children}
  </li>
);

function Recent({ options, active, setActive, onPick, onClear }) {
  return (
    <div data-testid="topbar-search-recent">
      <GroupHead right={<button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClear} data-testid="topbar-search-recent-clear"
        className="normal-case tracking-normal text-[11px] font-semibold hover:underline" style={{ color: '#4575A8' }}>Clear</button>}>Recent searches</GroupHead>
      <ul role="listbox" id="topbar-search-list">
        {options.map((o, i) => (
          <Option key={o.term} i={i} active={active} onPick={() => onPick(o)} onHover={() => setActive(i)} testid={`topbar-search-recent-${i}`}
            extra="flex items-center gap-2">
            <Clock className="w-3 h-3 shrink-0" style={{ color: 'var(--strata-text-muted)' }} />
            <span className="truncate" style={{ color: 'var(--strata-text-primary)' }}>{o.term}</span>
          </Option>
        ))}
      </ul>
    </div>
  );
}

function Group({ g, options, q, active, setActive, onPick }) {
  const k = g.source.key;
  return (
    <div data-testid={`topbar-search-group-${k}`} className="border-t first:border-t-0" style={{ borderColor: 'var(--strata-border)' }}>
      <GroupHead right={!g.error && <span className="normal-case tracking-normal">{g.total}</span>}>{g.source.title}</GroupHead>
      {g.error ? <Note testid={`topbar-search-error-${k}`}>{g.source.title} search isn't available right now. Try again.</Note> : (
        <ul role="group" aria-label={g.source.title}>
          {options.map(({ o, i }) => (o.kind === 'item' ? (
            <HeaderSearchRow key={o.it.id} id={`topbar-search-opt-${i}`} row={g.source.row(o.it)} q={q} active={active === i}
              testid={`topbar-search-row-${k}-${o.it.id}`} onPick={() => onPick(o)} onHover={() => setActive(i)} />
          ) : (
            <Option key="all" i={i} active={active} onPick={() => onPick(o)} onHover={() => setActive(i)} testid={`topbar-search-see-all-${k}`}
              extra="font-semibold"><span style={{ color: '#4575A8' }}>{g.all.label}</span></Option>
          )))}
        </ul>
      )}
    </div>
  );
}

// What the drop-down shows: recent searches (empty box), "Searching…", matches grouped by kind, or a plain message.
export default function HeaderSearchPanel({ q, res, ready, options, sources, active, setActive, onPick, onClearRecent }) {
  if (!q) return <Recent options={options} active={active} setActive={setActive} onPick={onPick} onClear={onClearRecent} />;
  if (!ready) return <Note testid="topbar-search-loading">Searching…</Note>;
  if (res.status === 'error') return <Note testid="topbar-search-error">Search isn't available right now. Try again.</Note>;
  const shown = res.groups.filter((g) => g.error || g.items.length);
  if (!shown.length) return <Note testid="topbar-search-empty">No {nounList(sources)} match '{q}'</Note>;
  const indexed = options.map((o, i) => ({ o, i }));
  return (
    <div id="topbar-search-list" role="listbox" className="max-h-[70vh] overflow-y-auto">
      {shown.map((g) => (
        <Group key={g.source.key} g={g} q={q} active={active} setActive={setActive} onPick={onPick}
          options={indexed.filter(({ o }) => o.g === g)} />
      ))}
    </div>
  );
}
